import React, { useState, useEffect, useRef } from 'react';
import io from 'socket.io-client';
import axios from 'axios';
import QRDisplay from './components/QRDisplay';
import Sidebar from './components/Sidebar';
import Login from './components/Login';

import ChatArea from './components/ChatArea';
import TagSelector from './components/TagSelector';
import TemplateSelector from './components/TemplateSelector';
import BroadcastFromSheetModal from './components/BroadcastFromSheetModal';
import './App.css';

const getApiUrl = () => {
  // En producción usar siempre el mismo origen (evita CORS www vs no-www)
  if (window.location.hostname !== 'localhost') {
    return window.location.origin;
  }
  return window.location.port === '3001' ? 'http://localhost:3000' : 'http://localhost:3000';
};

const API_URL = getApiUrl();
const WS_URL = API_URL;

function App() {
  // Auth State
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // App State
  const [isMobile, setIsMobile] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const selectedConversationRef = useRef(null);
  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);
  const [messages, setMessages] = useState([]);
  // Cache de mensajes por conversationId — persiste entre cambios de canal, solo se limpia al logout
  const messagesCacheRef = useRef(new Map()); // Map<convId, { msgs: [], hasMore: bool, loadingMore: bool }>
  const [msgCacheVersion, setMsgCacheVersion] = useState(0); // para forzar re-render cuando cambia el cache
  const [socket, setSocket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [tags, setTags] = useState([]);
  const [quickReplies, setQuickReplies] = useState([]);
  const [showQuickReplyModal, setShowQuickReplyModal] = useState(false);
  const [showQuickReplyManageModal, setShowQuickReplyManageModal] = useState(false);
  const [editingQuickReplyId, setEditingQuickReplyId] = useState(null);
  const [newQuickReplyText, setNewQuickReplyText] = useState('');
  const [newQuickReplyImageUrl, setNewQuickReplyImageUrl] = useState('');

  // Tema: modo oscuro (estilo WhatsApp)
  const [theme, setTheme] = useState(() => localStorage.getItem('crm-theme') || 'light');
  useEffect(() => {
    localStorage.setItem('crm-theme', theme);
    if (theme === 'dark') document.body.classList.add('dark');
    else document.body.classList.remove('dark');
  }, [theme]);
  // Filtro por etiqueta en el sidebar (desde panel Etiquetas)
  const [filterTag, setFilterTag] = useState('');
  const [filterChannel, setFilterChannel] = useState('');
  // Tag Selector State (Global)
  const [tagSelectorProps, setTagSelectorProps] = useState({
    show: false,
    mode: 'single', // 'single', 'bulk', 'manage'
    conversationId: null, // for 'single'
    selectedIds: [], // for 'bulk'
    selectedTags: [] // initial selected tags for the UI
  });

  // Template Selector State (Global)
  const [templateSelectorProps, setTemplateSelectorProps] = useState({
    show: false,
    selectedIds: []
  });
  const [showBroadcastFromSheet, setShowBroadcastFromSheet] = useState(false);
  // Enviar mismo mensaje a contactos seleccionados (como WhatsApp)
  const [sendMessageToSelectedProps, setSendMessageToSelectedProps] = useState({
    show: false,
    conversations: []
  });
  const [bulkMessageText, setBulkMessageText] = useState('');

  // Broadcast masivo: control de estado
  const [isBroadcasting, setIsBroadcasting] = useState(false);
  const [broadcastProgress, setBroadcastProgress] = useState(null);
  const broadcastCancelRef = useRef(false);

  // Mensajes automáticos (bienvenida + fuera de horario)
  const [showAutoReplyModal, setShowAutoReplyModal] = useState(false);
  const [autoReplySettings, setAutoReplySettings] = useState(null);
  const [autoReplySaving, setAutoReplySaving] = useState(false);

  // 1. Check Auth on Mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    if (token && storedUser) {
      setUser(JSON.parse(storedUser));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    setAuthLoading(false);
  }, []);

  // Login Handler
  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
    // Reload stuff
    loadConversations();
    loadTags();
    loadQuickReplies();
    loadAutoReplySettings();
  };

  // Logout Handler
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionStorage.removeItem('loadedConversationsCount');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setSelectedConversation(null);
    setConversations([]);
    setMessages([]);
    messagesCacheRef.current = new Map(); // Limpiar cache al cerrar sesión
  };

  // Detect Mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768 || /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Initialize Socket.io (Only if user is logged in)
  useEffect(() => {
    if (!user) return;

    // Send token in auth for socket handshake (if backend supports it, nice to have)
    const newSocket = io(WS_URL, {
      auth: { token: localStorage.getItem('token') },
      transports: ['polling', 'websocket']
    });
    setSocket(newSocket);

    newSocket.on('connect', () => {
      console.log('✅ Conectado al servidor WebSocket');
    });

    newSocket.on('disconnect', () => {
      console.log('❌ Desconectado del servidor WebSocket');
    });

    // New Message
    newSocket.on('new_message', (data) => {
      // 1. Update messages in active chat AND cache
      const cache = messagesCacheRef.current;
      if (cache.has(data.conversationId)) {
        const entry = cache.get(data.conversationId);
        if (!entry.msgs.some(m => m.id === data.message.id)) {
          cache.set(data.conversationId, { ...entry, msgs: [...entry.msgs, data.message] });
          setMsgCacheVersion(v => v + 1);
        }
      }
      if (selectedConversationRef.current && data.conversationId === selectedConversationRef.current.id) {
        setMessages(prev => {
          if (prev.some(m => m.id === data.message.id)) return prev;
          return [...prev, data.message];
        });
      }

      // 2. Update conversation list (añadir conversación nueva si no está)
      setConversations(prev => {
        const index = prev.findIndex(c => c.id === data.conversationId);

        if (index === -1) {
          // Conversación nueva: añadirla para que se vea ya (luego loadConversations refresca)
          const conv = data.message.conversation;
          const newConv = {
            id: data.conversationId,
            wa_id: conv?.waId || '',
            name: conv?.name || conv?.waId || '',
            last_message: data.message.body,
            last_message_at: data.message.createdAt,
            last_message_direction: 'incoming',
            unread_messages: (!selectedConversationRef.current || selectedConversationRef.current.id !== data.conversationId) ? 1 : 0,
            tags: []
          };
          loadConversations(false);
          return [newConv, ...prev];
        }

        const updatedConv = { ...prev[index] };
        updatedConv.last_message = data.message.body;
        // Check timestamps to avoid replacing with older if out of order (rare)
        const msgTime = data.message.createdAt ? new Date(data.message.createdAt).getTime() : Date.now();
        const convTime = updatedConv.last_message_at ? new Date(updatedConv.last_message_at).getTime() : 0;

        if (msgTime >= convTime) {
          updatedConv.last_message_at = data.message.createdAt || new Date().toISOString();
        }

        // Increment unread count if incoming and not currently open
        if (data.message.direction === 'incoming') {
          if (!selectedConversationRef.current || selectedConversationRef.current.id !== data.conversationId) {
            updatedConv.unread_messages = (parseInt(updatedConv.unread_messages) || 0) + 1;
          }
        }

        // Move to top
        const newPrev = [...prev];
        newPrev.splice(index, 1);
        newPrev.unshift(updatedConv);
        return newPrev;
      });
    });

    // Conversation Updated (e.g. status change, channel change, name change)
    newSocket.on('conversation_updated', (data) => {
      setConversations(prev => {
        const index = prev.findIndex(c => c.id === data.conversationId);
        if (index === -1) return prev;

        const updated = { ...prev[index], ...data };
        const newPrev = [...prev];
        newPrev[index] = updated;
        return newPrev;
      });

      // Also update selectedConversation if it matches
      if (selectedConversationRef.current && selectedConversationRef.current.id === data.conversationId) {
        setSelectedConversation(prev => ({ ...prev, ...data }));
      }
    });

    // Reaction Received
    newSocket.on('reaction_received', (data) => {
      if (selectedConversationRef.current && data.conversationId === selectedConversationRef.current.id) {
        setMessages(prev => prev.map(m =>
          m.id === data.messageId ? { ...m, reaction: data.reaction } : m
        ));
      }
    });

    // Message Revoked
    newSocket.on('message_revoked', (data) => {
      if (selectedConversationRef.current && data.conversationId === selectedConversationRef.current.id) {
        setMessages(prev => prev.map(m =>
          m.id === data.id ? { ...m, status: 'revoked', body: '🚫 Mensaje eliminado' } : m
        ));
      }

      // Update conversation list last message if it was the revoked one
      setConversations(prev => {
        const index = prev.findIndex(c => c.id === data.conversationId);
        if (index === -1) return prev;

        // Helper: we don't know if this was the last message without fetching, 
        // but we can at least update it if we are looking at it or just let loadConversations handle it later.
        // For now, let's just leave conversation list as is, or maybe mark it. 
        // Realistically, "Message revoked" is the new body text.
        return prev;
      });
    });

    // Message Updated (Edited)
    newSocket.on('message_updated', (data) => {
      if (selectedConversationRef.current && data.conversationId === selectedConversationRef.current.id) {
        setMessages(prev => prev.map(m =>
          m.id === data.id ? { ...m, body: data.newBody, status: data.status || m.status } : m
        ));
      }

      // Update conversation list preview
      setConversations(prev => {
        const index = prev.findIndex(c => c.id === data.conversationId);
        if (index === -1) return prev;
        const updated = { ...prev[index] };
        // Only update preview if it was the last message
        if (updated.last_message === data.oldBody || true) {
          updated.last_message = data.newBody;
        }
        const newPrev = [...prev];
        newPrev[index] = updated;
        return newPrev;
      });
    });

    // Todas marcadas como leídas (desde otro lugar o acción)
    newSocket.on('conversations_all_read', () => {
      setConversations(prev => prev.map(c => ({ ...c, unread_messages: 0 })));
    });

    return () => {
      newSocket.close();
    };
  }, [user]);

  // Refresco periódico de conversaciones por si el socket no llega (ej. webhook en otro servidor)
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => {
      // Si hay una búsqueda activa, evitar el refresco periódico automático para no interferir ni saturar la DB
      if (searchTermRef.current && searchTermRef.current.trim() !== '') {
        return;
      }
      // Solo refrescar lista de conversaciones; los mensajes llegan via socket
      loadConversations(false);
    }, 20000);
    return () => clearInterval(interval);
  }, [user]);

  // Toast Timer
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // Load Data Effects
  useEffect(() => {
    if (user) {
      loadConversations();
      loadTags();
      loadQuickReplies();
      loadAutoReplySettings();
    }
  }, [user]);

  useEffect(() => {
    if (selectedConversation) {
      const cached = messagesCacheRef.current.get(selectedConversation.id);
      if (cached) {
        // Ya está en cache: mostrar de inmediato sin hacer fetch
        setMessages(cached.msgs);
      } else {
        loadMessages(selectedConversation.id);
      }
    } else {
      setMessages([]);
    }
  }, [selectedConversation?.id]);

  // --- API Handlers ---

  const loadTags = async (channelId = filterChannel) => {
    try {
      const chParam = channelId ? `?channelId=${encodeURIComponent(channelId)}` : '';
      const res = await axios.get(`${API_URL}/api/tags${chParam}`);
      setTags(res.data);
    } catch (error) {
      console.error('Error cargando etiquetas:', error);
      if (error.response && (error.response.status === 401 || error.response.status === 403)) handleLogout();
    }
  };

  const loadQuickReplies = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/quick-replies`);
      setQuickReplies(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Error cargando respuestas rápidas:', error);
      setQuickReplies([]);
      if (error.response && (error.response.status === 401 || error.response.status === 403)) handleLogout();
    }
  };

  const handleAddQuickReply = async (text, imageUrl = '') => {
    try {
      const payload = { text };
      if (imageUrl && String(imageUrl).trim()) payload.image_url = String(imageUrl).trim();
      const res = await axios.post(`${API_URL}/api/quick-replies`, payload);
      setQuickReplies(prev => [res.data, ...(Array.isArray(prev) ? prev : [])]);
      setToast({ message: 'Respuesta rápida guardada', type: 'success' });
    } catch (error) {
      console.error('Error guardando respuesta rápida:', error);
      setToast({ message: 'Error al guardar', type: 'error' });
    }
  };

  const handleSendImageByUrl = async (conversationId, imageUrl, caption, replyTo = null) => {
    if (!conversationId || !imageUrl?.trim()) return;
    try {
      const res = await axios.post(`${API_URL}/api/messages/send-image-url`, {
        conversationId,
        imageUrl: imageUrl.trim(),
        caption: caption || '',
        replyToMessageId: replyTo ? replyTo.id : null
      });
      if (res.data.message && selectedConversation && selectedConversation.id === conversationId) {
        const saved = res.data.message;
        const newMsg = {
          id: saved.id,
          body: saved.body,
          type: 'image',
          direction: 'outgoing',
          status: saved.status ?? 'sent',
          created_at: saved.createdAt,
          reply_to_message_id: replyTo ? replyTo.id : null
        };
        setMessages(prev => (prev.some(m => m.id === newMsg.id) ? prev : [...prev, newMsg]));
      }
    } catch (error) {
      console.error('Error enviando imagen:', error);
      alert('Error enviando imagen: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleUpdateQuickReply = async (id, text, imageUrl = '') => {
    try {
      const payload = { text };
      if (imageUrl !== undefined) payload.image_url = imageUrl && String(imageUrl).trim() ? String(imageUrl).trim() : null;
      const res = await axios.put(`${API_URL}/api/quick-replies/${id}`, payload);
      setQuickReplies(prev => (Array.isArray(prev) ? prev : []).map(q => q.id === id ? res.data : q));
      setToast({ message: 'Respuesta rápida actualizada', type: 'success' });
    } catch (error) {
      console.error('Error actualizando respuesta rápida:', error);
      setToast({ message: 'Error al actualizar', type: 'error' });
    }
  };

  const handleDeleteQuickReply = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/quick-replies/${id}`);
      setQuickReplies(prev => (Array.isArray(prev) ? prev : []).filter(q => q.id !== id));
      setToast({ message: 'Respuesta rápida eliminada', type: 'success' });
    } catch (error) {
      console.error('Error eliminando respuesta rápida:', error);
      setToast({ message: 'Error al eliminar', type: 'error' });
    }
  };

  const openNewQuickReplyForm = () => {
    setEditingQuickReplyId(null);
    setNewQuickReplyText('');
    setNewQuickReplyImageUrl('');
    setShowQuickReplyManageModal(false);
    setShowQuickReplyModal(true);
  };

  const openEditQuickReplyForm = (q) => {
    setEditingQuickReplyId(q.id);
    setNewQuickReplyText(q.text || '');
    setNewQuickReplyImageUrl(q.image_url || '');
    setShowQuickReplyManageModal(false);
    setShowQuickReplyModal(true);
  };

  const [searchTerm, setSearchTerm] = useState('');
  const [hasMoreConversations, setHasMoreConversations] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const BATCH_SIZE = 800;

  const searchTermRef = useRef('');
  const filterChannelRef = useRef('');
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    searchTermRef.current = searchTerm;
  }, [searchTerm]);

  useEffect(() => {
    filterChannelRef.current = filterChannel;
  }, [filterChannel]);

  const loadConversations = async (showLoading = true, channel = null, search = null) => {
    try {
      if (showLoading) setLoading(true);
      const savedCount = parseInt(sessionStorage.getItem('loadedConversationsCount'), 10) || BATCH_SIZE;
      const fetchLimit = Math.max(BATCH_SIZE, savedCount);

      const activeChannel = channel !== null ? channel : filterChannelRef.current;
      const activeSearch = search !== null ? search : searchTermRef.current;

      const chParam = activeChannel ? `&channelId=${encodeURIComponent(activeChannel)}` : '';
      const sParam = activeSearch ? `&search=${encodeURIComponent(activeSearch)}` : '';
      const res = await axios.get(`${API_URL}/api/conversations?limit=${fetchLimit}&offset=0${chParam}${sParam}`);
      setConversations(res.data);
      sessionStorage.setItem('loadedConversationsCount', res.data.length);
      setHasMoreConversations(res.data.length >= fetchLimit);
    } catch (error) {
      console.error('Error cargando conversaciones:', error);
      if (error.response && (error.response.status === 401 || error.response.status === 403)) handleLogout();
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const loadMoreConversations = async () => {
    if (loadingMore || !hasMoreConversations) return;
    try {
      setLoadingMore(true);
      const offset = conversations.length;
      const activeChannel = filterChannelRef.current;
      const activeSearch = searchTermRef.current;

      const chParam = activeChannel ? `&channelId=${encodeURIComponent(activeChannel)}` : '';
      const sParam = activeSearch ? `&search=${encodeURIComponent(activeSearch)}` : '';
      const res = await axios.get(`${API_URL}/api/conversations?limit=${BATCH_SIZE}&offset=${offset}${chParam}${sParam}`);
      if (res.data.length < BATCH_SIZE) {
        setHasMoreConversations(false);
      }
      if (res.data.length > 0) {
        setConversations(prev => {
          const existingIds = new Set(prev.map(c => c.id));
          const newItems = res.data.filter(c => !existingIds.has(c.id));
          const updated = [...prev, ...newItems];
          sessionStorage.setItem('loadedConversationsCount', updated.length);
          return updated;
        });
      }
    } catch (error) {
      console.error('Error cargando más conversaciones:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleChannelChange = (newChannel) => {
    setFilterChannel(newChannel);
    // NO limpiar conversación activa — los mensajes persisten en el cache
    loadConversations(true, newChannel, searchTermRef.current);
    loadTags(newChannel);
  };

  const handleSearchChange = (newSearch) => {
    setSearchTerm(newSearch);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      loadConversations(false, filterChannelRef.current, newSearch);
    }, 500);
  };

  const MSG_PAGE_SIZE = 50;

  const loadMessages = async (conversationId) => {
    try {
      const res = await axios.get(`${API_URL}/api/conversations/${conversationId}/messages?limit=${MSG_PAGE_SIZE}`);
      const msgs = res.data;
      const hasMore = msgs.length >= MSG_PAGE_SIZE;
      messagesCacheRef.current.set(conversationId, { msgs, hasMore, loadingMore: false });
      setMsgCacheVersion(v => v + 1);
      setMessages(msgs);
    } catch (error) {
      console.error('Error cargando mensajes:', error);
      if (error.response && (error.response.status === 401 || error.response.status === 403)) handleLogout();
    }
  };

  const loadMoreMessages = async (conversationId) => {
    const cache = messagesCacheRef.current;
    const entry = cache.get(conversationId);
    if (!entry || entry.loadingMore || !entry.hasMore) return;

    // Marcar como cargando
    cache.set(conversationId, { ...entry, loadingMore: true });
    setMsgCacheVersion(v => v + 1);

    try {
      // Buscar el ID del mensaje más antiguo que tenemos
      const oldestId = entry.msgs.length > 0 ? entry.msgs[0].id : null;
      const beforeParam = oldestId ? `&before=${oldestId}` : '';
      const res = await axios.get(
        `${API_URL}/api/conversations/${conversationId}/messages?limit=${MSG_PAGE_SIZE}${beforeParam}`
      );
      const older = res.data;
      const allMsgs = [...older, ...entry.msgs];
      const hasMore = older.length >= MSG_PAGE_SIZE;
      cache.set(conversationId, { msgs: allMsgs, hasMore, loadingMore: false });
      setMsgCacheVersion(v => v + 1);
      if (selectedConversationRef.current?.id === conversationId) {
        setMessages(allMsgs);
      }
    } catch (error) {
      console.error('Error cargando más mensajes:', error);
      cache.set(conversationId, { ...entry, loadingMore: false });
      setMsgCacheVersion(v => v + 1);
    }
  };

  const handleSelectConversation = async (conv) => {
    setSelectedConversation(conv);
    try {
      if (conv.unread_messages > 0) {
        await axios.post(`${API_URL}/api/conversations/${conv.id}/read`);
        setConversations(prev => prev.map(c =>
          c.id === conv.id ? { ...c, unread_messages: 0 } : c
        ));
      }
    } catch (error) {
      console.error('Error marcando como leído:', error);
    }
  };

  const handleMarkAllAsRead = async (channelId) => {
    try {
      await axios.post(`${API_URL}/api/conversations/read-all`, { channelId: channelId || undefined });
      setConversations(prev => prev.map(c =>
        !channelId || c.channel_id === channelId ? { ...c, unread_messages: 0 } : c
      ));
      setToast({ message: channelId ? 'Conversaciones del canal marcadas como leídas' : 'Todas marcadas como leídas', type: 'success' });
    } catch (error) {
      console.error('Error marcando como leídas:', error);
      setToast({ message: 'Error al marcar como leídas', type: 'error' });
    }
  };

  const handleSendMessage = async (messageText, replyTo = null) => {
    if (!messageText.trim() || !selectedConversation) return;

    try {
      const res = await axios.post(`${API_URL}/api/send`, {
        to: selectedConversation.wa_id,
        message: messageText,
        conversationId: selectedConversation.id,
        replyTo: replyTo ? replyTo.id : null
      });
      // Solo añadir a la lista si el servidor confirma éxito (Meta aceptó el envío)
      if (res.data.success && res.data.message && selectedConversation) {
        const saved = res.data.message;
        const newMsg = {
          id: saved.id,
          body: saved.body ?? messageText,
          type: saved.type ?? 'text',
          direction: 'outgoing',
          status: saved.status ?? 'sent',
          created_at: saved.created_at,
          wam_id: saved.wam_id || null,
          reply_to_message_id: saved.reply_to_message_id ?? (replyTo ? replyTo.id : null)
        };
        setMessages(prev => {
          if (prev.some(m => m.id === newMsg.id)) return prev;
          return [...prev, newMsg];
        });
      }
    } catch (error) {
      console.error('Error enviando mensaje:', error);
      const data = error.response?.data;
      const details = data?.details || data?.error || error.message;
      setToast({ message: details, type: 'error' });
    }
  };

  const handleUpdateTags = async (conversationId, tagIds) => {
    try {
      const conv = conversations.find(c => c.id === conversationId);
      const currentTagIds = conv.tags ? conv.tags.map(t => t.id) : [];

      const toAdd = tagIds.filter(id => !currentTagIds.includes(id));
      const toRemove = currentTagIds.filter(id => !tagIds.includes(id));

      for (const id of toAdd) {
        await axios.post(`${API_URL}/api/conversations/${conversationId}/tags`, { tagId: id });
      }
      for (const id of toRemove) {
        await axios.delete(`${API_URL}/api/conversations/${conversationId}/tags/${id}`);
      }

      loadConversations();
      if (selectedConversation && selectedConversation.id === conversationId) {
        const updatedConv = (await axios.get(`${API_URL}/api/conversations/${conversationId}`)).data;
        setSelectedConversation(updatedConv);
      }
    } catch (error) {
      console.error('Error actualizando etiquetas:', error);
      alert('Error updating tags');
    }
  };

  const handleCreateTag = async (name, color) => {
    try {
      await axios.post(`${API_URL}/api/tags`, { name, color });
      loadTags();
    } catch (error) {
      console.error('Error creando etiqueta:', error);
      alert('Error creando etiqueta');
    }
  };

  const handleEditTag = async (id, name, color) => {
    try {
      await axios.put(`${API_URL}/api/tags/${id}`, { name, color });
      loadTags();
    } catch (error) {
      console.error('Error editando etiqueta:', error);
      alert('Error editando etiqueta');
    }
  };

  const handleDeleteTag = async (tagId) => {
    try {
      await axios.delete(`${API_URL}/api/tags/${tagId}`);
      loadTags();
    } catch (error) {
      alert('Error eliminando etiqueta');
    }
  };

  const handleExportTag = (tagId) => {
    axios.get(`${API_URL}/api/tags/${tagId}/export`, { responseType: 'blob' })
      .then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `contactos_${tagId}.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch(async (error) => {
        console.error("Error exportando", error);
        let errorMsg = "Error descargando el archivo";
        if (error.response && error.response.data instanceof Blob) {
          try {
            const text = await error.response.data.text();
            const json = JSON.parse(text);
            if (json.error) errorMsg = json.error;
          } catch (e) {}
        }
        alert(errorMsg);
      });
  };

  const handleUpdateChannel = async (conversationId, newChannelId) => {
    try {
      await axios.put(`${API_URL}/api/conversations/${conversationId}/channel`, { channelId: newChannelId });
      loadConversations();
      if (selectedConversation && selectedConversation.id === conversationId) {
        const updatedConv = (await axios.get(`${API_URL}/api/conversations/${conversationId}`)).data;
        setSelectedConversation(updatedConv);
      }
    } catch (error) {
      alert('Error actualizando canal');
    }
  };

  const handleUpdateConversationName = async (conversationId, name) => {
    try {
      const updated = (await axios.put(`${API_URL}/api/conversations/${conversationId}`, { name })).data;
      setSelectedConversation(updated);
      loadConversations();
    } catch (error) {
      console.error('Error actualizando nombre:', error);
      setToast({ message: 'Error al guardar el nombre', type: 'error' });
    }
  };

  const handleSendReaction = async (msgId, reaction) => {
    const msg = messages.find(m => m.id === msgId);
    const wamId = msg?.wam_id || msg?.wamId;
    if (!msg || !wamId) return alert('No se puede reaccionar a este mensaje (falta ID de WhatsApp)');

    try {
      await axios.post(`${API_URL}/api/messages/${msgId}/reaction`, {
        reaction, wamId, conversationId: selectedConversation.id
      });
      setMessages(prev => prev.map(m => m.id === msgId ? { ...m, reaction } : m));
    } catch (error) {
      console.error('Error enviando reacción:', error);
      alert('Error enviando reacción: ' + (error.response?.data?.error || error.message));
    }
  };

  // --- UI Handlers ---

  const openTagSelector = ({ mode = 'manage', conversationId = null, selectedIds = [], initialSelectedTags = [] }) => {
    setTagSelectorProps({ show: true, mode, conversationId, selectedIds, selectedTags: initialSelectedTags });
  };
  const closeTagSelector = () => setTagSelectorProps(prev => ({ ...prev, show: false }));

  const handleSaveTagSelection = async (finalTagIds) => {
    const { mode, conversationId, selectedIds } = tagSelectorProps;
    if (mode === 'single' && conversationId) {
      await handleUpdateTags(conversationId, finalTagIds);
    } else if (mode === 'bulk' && selectedIds.length > 0) {
      for (const convId of selectedIds) {
        await handleUpdateTags(convId, finalTagIds);
      }
    }
    closeTagSelector();
  };

  const openBroadcast = ({ selectedIds }) => {
    const selectedConvs = conversations.filter(c => selectedIds.includes(c.id));
    setTemplateSelectorProps({ show: true, selectedConversations: selectedConvs });
  };
  const closeBroadcast = () => setTemplateSelectorProps(prev => ({ ...prev, show: false }));

  const openBroadcastFromSheet = () => setShowBroadcastFromSheet(true);
  const closeBroadcastFromSheet = () => setShowBroadcastFromSheet(false);

  const openSendMessageToSelected = ({ selectedIds }) => {
    const selectedConvs = conversations.filter(c => selectedIds.includes(c.id));
    setSendMessageToSelectedProps({ show: true, conversations: selectedConvs });
  };
  const closeSendMessageToSelected = () => setSendMessageToSelectedProps(prev => ({ ...prev, show: false }));

  const loadAutoReplySettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/auto-reply-settings`);
      setAutoReplySettings(res.data);
    } catch (e) {
      console.error('Error cargando mensajes automáticos:', e);
      setToast({ message: 'Error al cargar configuración', type: 'error' });
      setAutoReplySettings({
        welcome_enabled: false,
        welcome_message: '',
        away_enabled: false,
        away_message: '',
        business_hours_start: '09:00',
        business_hours_end: '18:00',
        timezone: 'America/La_Paz'
      });
    }
  };

  const saveAutoReplySettings = async (data) => {
    setAutoReplySaving(true);
    try {
      const res = await axios.put(`${API_URL}/api/auto-reply-settings`, data);
      setAutoReplySettings(res.data || { ...autoReplySettings, ...data });
      setToast({ message: 'Configuración guardada', type: 'success' });
      setShowAutoReplyModal(false);
    } catch (e) {
      console.error('Error guardando mensajes automáticos:', e);
      setToast({ message: 'Error al guardar', type: 'error' });
    } finally {
      setAutoReplySaving(false);
    }
  };

  const resetAutoReplySettings = async () => {
    if (!window.confirm('¿Restablecer mensajes automáticos a valores por defecto y desactivar ambos?')) return;
    setAutoReplySaving(true);
    try {
      const res = await axios.post(`${API_URL}/api/auto-reply-settings/reset`);
      setAutoReplySettings(res.data);
      setToast({ message: 'Restablecido por defecto', type: 'success' });
    } catch (e) {
      console.error('Error restableciendo:', e);
      setToast({ message: 'Error al restablecer', type: 'error' });
    } finally {
      setAutoReplySaving(false);
    }
  };

  const disableAllAutoReplies = () => {
    setAutoReplySettings(s => ({ ...s, welcome_enabled: false, away_enabled: false }));
    setToast({ message: 'Desactivados en el formulario. Pulsa Guardar para aplicar.', type: 'success' });
  };

  const handleToggleConversationBot = async (conversationId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'bot' ? 'human' : 'bot';
      await axios.put(`${API_URL}/api/conversations/${conversationId}/bot-status`, { botStatus: newStatus });
      // Update local state
      setConversations(prev => prev.map(c => c.id === conversationId ? { ...c, bot_status: newStatus } : c));
      if (selectedConversation && selectedConversation.id === conversationId) {
        setSelectedConversation(prev => ({ ...prev, bot_status: newStatus }));
      }
    } catch (error) {
      console.error('Error toggling bot status:', error);
      setToast({ message: 'Error al cambiar estado del bot', type: 'error' });
    }
  };

  const handleSendMessageToSelected = async (messageText) => {
    const { conversations: convs } = sendMessageToSelectedProps;
    if (!messageText.trim() || !convs.length) return;
    setLoading(true);
    let sent = 0;
    let failed = 0;
    try {
      for (const conv of convs) {
        try {
          await axios.post(`${API_URL}/api/send`, {
            to: conv.wa_id,
            message: messageText.trim(),
            conversationId: conv.id
          });
          sent++;
        } catch (err) {
          console.error('Error enviando a', conv.wa_id, err);
          failed++;
        }
      }
      closeSendMessageToSelected();
      setBulkMessageText('');
      if (failed === 0) {
        setToast({ message: `Mensaje enviado a ${sent} contacto${sent !== 1 ? 's' : ''}`, type: 'success' });
      } else {
        setToast({ message: `Enviado a ${sent}, fallaron ${failed}`, type: 'error' });
      }
      loadConversations();
    } catch (error) {
      setToast({ message: 'Error al enviar: ' + (error.response?.data?.error || error.message), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSendBroadcast = async (template, finalRecipients, components, channelId, force = false) => {
    if (!finalRecipients?.length) return;
    // Evitar envíos duplicados (doble-click u otras causas)
    if (isBroadcasting) return;

    setIsBroadcasting(true);
    broadcastCancelRef.current = false;
    setBroadcastProgress({ current: 0, total: finalRecipients.length, sent: 0, failed: 0 });

    try {
      const recipientsPayload = finalRecipients.map(r => {
        const isTemp = r.id && (String(r.id).startsWith('temp_') || String(r.id).startsWith('sheet_'));
        return {
          id: isTemp ? null : r.id,
          wa_id: r.wa_id,
          name: r.name
        };
      });

      // Enviar al backend (que procesa en lote de forma controlada)
      const res = await axios.post(`${API_URL}/api/broadcast`, {
        recipients: recipientsPayload,
        templateName: template.name,
        language: template.language,
        components,
        channelId,
        force: !!force
      });

      if (broadcastCancelRef.current) {
        setToast({ message: 'Difusión cancelada por el usuario.', type: 'error' });
        return;
      }

      const { sent, failed, details } = res.data || {};
      setBroadcastProgress({ current: finalRecipients.length, total: finalRecipients.length, sent: sent || 0, failed: failed || 0 });

      if (sent > 0 && failed === 0) {
        const skipped = (details || []).filter(d => d.messageId === 'duplicate_skipped').length;
        const actualSent = sent - skipped;
        const msgParts = [];
        if (actualSent > 0) msgParts.push(`Enviado a ${actualSent} contacto${actualSent !== 1 ? 's' : ''} 🚀`);
        if (skipped > 0) msgParts.push(`${skipped} ya recibieron el mensaje recientemente`);
        setToast({ message: msgParts.join(' · '), type: actualSent > 0 ? 'success' : 'error' });
        closeBroadcast();
        closeBroadcastFromSheet();
      } else if (sent > 0 && failed > 0) {
        setToast({ message: `Enviado a ${sent}, fallaron ${failed}.`, type: 'error' });
      } else {
        const raw = (details && details.find(d => !d.success)?.error) || res.data?.error;
        const firstError = typeof raw === 'string' ? raw : (raw?.error?.message || raw?.message || (raw && JSON.stringify(raw)) || 'No se envió a ningún contacto');
        setToast({ message: `Difusión falló: ${firstError}`, type: 'error' });
      }
      loadConversations();
    } catch (error) {
      if (broadcastCancelRef.current) {
        setToast({ message: 'Difusión cancelada.', type: 'error' });
        return;
      }
      const d = error.response?.data;
      let msg = error.message;
      if (d) {
        if (typeof d === 'string') msg = d;
        else if (d.error) msg = typeof d.error === 'string' ? d.error : (d.error.message || d.error.error_user_msg || JSON.stringify(d.error));
        else if (d.details) msg = d.details;
        else msg = d.message || JSON.stringify(d);
      }
      setToast({ message: `Error: ${msg}`, type: 'error' });
    } finally {
      setIsBroadcasting(false);
      setBroadcastProgress(null);
      broadcastCancelRef.current = false;
    }
  };

  const handleCancelBroadcast = () => {
    broadcastCancelRef.current = true;
    setToast({ message: 'Cancelando difusión...', type: 'error' });
  };

  // --- RENDER ---

  if (authLoading) return <div className="loading-container">Cargando...</div>;
  if (!user) return <Login onLogin={handleLogin} />;

  if (isMobile && !selectedConversation) {
    return <QRDisplay />;
  }

  return (
    <div className={`whatsapp-container ${theme === 'dark' ? 'dark' : ''}`}>
      <Sidebar
        conversations={conversations}
        selectedConversation={selectedConversation}
        onSelectConversation={handleSelectConversation}
        loading={loading}
        onLoadMore={loadMoreConversations}
        hasMore={hasMoreConversations}
        loadingMore={loadingMore}
        tags={tags}
        filterTag={filterTag}
        setFilterTag={setFilterTag}
        filterChannel={filterChannel}
        onChannelChange={handleChannelChange}
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        onOpenTagSelector={openTagSelector}
        onOpenBroadcast={openBroadcast}
        onOpenBroadcastFromSheet={openBroadcastFromSheet}
        onOpenSendMessageToSelected={openSendMessageToSelected}
        onMarkAllAsRead={handleMarkAllAsRead}
        onOpenQuickReplyModal={() => setShowQuickReplyManageModal(true)}
        onOpenAutoReplyModal={() => { setShowAutoReplyModal(true); loadAutoReplySettings(); }}
        user={user}
        onLogout={handleLogout}
        theme={theme}
        onToggleTheme={() => setTheme(t => t === 'dark' ? 'light' : 'dark')}
        globalBotEnabled={autoReplySettings ? autoReplySettings.global_bot_enabled : undefined}
        onToggleGlobalBot={() => {
          if (!autoReplySettings) return;
          const newVal = !autoReplySettings.global_bot_enabled;
          setAutoReplySettings(s => ({ ...s, global_bot_enabled: newVal }));
          saveAutoReplySettings({ ...autoReplySettings, global_bot_enabled: newVal });
        }}
        isMobile={isMobile}
      />
      <ChatArea
        conversation={selectedConversation}
        messages={messages}
        onSendMessage={handleSendMessage}
        isMobile={isMobile}
        onBack={() => setSelectedConversation(null)}
        tags={tags}
        onUpdateTags={handleUpdateTags}
        onCreateTag={handleCreateTag}
        onDeleteTag={handleDeleteTag}
        onUpdateChannel={handleUpdateChannel}
        onOpenTagSelector={openTagSelector}
        onSendReaction={handleSendReaction}
        quickReplies={quickReplies}
        onDeleteQuickReply={handleDeleteQuickReply}
        onSendImageByUrl={handleSendImageByUrl}
        onToggleBotStatus={handleToggleConversationBot}
        globalBotEnabled={autoReplySettings ? autoReplySettings.global_bot_enabled : undefined}
        onLoadMoreMessages={loadMoreMessages}
        hasMoreMessages={selectedConversation ? (messagesCacheRef.current.get(selectedConversation.id)?.hasMore ?? false) : false}
        loadingMoreMessages={selectedConversation ? (messagesCacheRef.current.get(selectedConversation.id)?.loadingMore ?? false) : false}
      />

      {tagSelectorProps.show && (
        <TagSelector
          mode={tagSelectorProps.mode}
          tags={tags}
          conversations={conversations}
          selectedTags={tagSelectorProps.selectedTags}
          onSave={handleSaveTagSelection}
          onClose={closeTagSelector}
          onCreateTag={handleCreateTag}
          onEditTag={handleEditTag}
          onDeleteTag={handleDeleteTag}
          onExportTag={handleExportTag}
          filterTag={filterTag}
          onSelectTagForFilter={(tagId) => { setFilterTag(String(tagId)); closeTagSelector(); }}
        />
      )}

      {templateSelectorProps.show && (
        <TemplateSelector
          initialRecipients={templateSelectorProps.selectedConversations}
          onSelect={handleSendBroadcast}
          onClose={isBroadcasting ? undefined : closeBroadcast}
          isBroadcasting={isBroadcasting}
          broadcastProgress={broadcastProgress}
          onCancelBroadcast={handleCancelBroadcast}
          theme={theme}
        />
      )}

      {showBroadcastFromSheet && (
        <BroadcastFromSheetModal
          onSelect={handleSendBroadcast}
          onClose={isBroadcasting ? undefined : closeBroadcastFromSheet}
          isBroadcasting={isBroadcasting}
          broadcastProgress={broadcastProgress}
          onCancelBroadcast={handleCancelBroadcast}
          theme={theme}
        />
      )}

      {/* Modal: Enviar mismo mensaje a contactos seleccionados (como WhatsApp) */}
      {sendMessageToSelectedProps.show && sendMessageToSelectedProps.conversations.length > 0 && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => { closeSendMessageToSelected(); setBulkMessageText(''); }}
        >
          <div
            style={{ background: '#fff', borderRadius: '14px', padding: '20px', width: '90%', maxWidth: '420px', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}
            onClick={e => e.stopPropagation()}
          >
            <h4 style={{ margin: '0 0 4px', fontSize: '17px' }}>Enviar mismo mensaje</h4>
            <p style={{ margin: '0 0 14px', fontSize: '13px', color: '#667781' }}>
              Se enviará el mismo mensaje de texto a <strong>{sendMessageToSelectedProps.conversations.length} contacto(s)</strong>.
            </p>
            <textarea
              value={bulkMessageText}
              onChange={e => setBulkMessageText(e.target.value)}
              placeholder="Escribe el mensaje que recibirán todos..."
              rows={4}
              disabled={loading}
              style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #e9edef', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box' }}
              autoFocus
            />
            <div style={{ marginTop: '10px', maxHeight: '120px', overflowY: 'auto', padding: '8px', background: '#f7f8f9', borderRadius: '8px', fontSize: '13px', color: '#54656f' }}>
              {sendMessageToSelectedProps.conversations.map(c => (
                <div key={c.id} style={{ padding: '4px 0' }}>• {c.name || c.wa_id}</div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
              <button
                type="button"
                onClick={() => { closeSendMessageToSelected(); setBulkMessageText(''); }}
                disabled={loading}
                style={{ padding: '10px 18px', borderRadius: '10px', border: '1px solid #e9edef', background: '#fff', cursor: 'pointer', fontWeight: '500' }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => handleSendMessageToSelected(bulkMessageText)}
                disabled={loading || !bulkMessageText.trim()}
                style={{ padding: '10px 20px', borderRadius: '10px', border: 'none', background: '#00a884', color: '#fff', cursor: 'pointer', fontWeight: '600' }}
              >
                {loading ? 'Enviando...' : `Enviar a ${sendMessageToSelectedProps.conversations.length}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Mensajes automáticos (bienvenida + fuera de horario) */}
      {showAutoReplyModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setShowAutoReplyModal(false)}
        >
          <div
            style={{ background: '#fff', borderRadius: '14px', padding: '20px', width: '90%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}
            onClick={e => e.stopPropagation()}
          >
            <h4 style={{ margin: '0 0 4px', fontSize: '17px' }}>🤖 Mensajes automáticos</h4>
            <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#667781' }}>
              Bienvenida: se envía al primer mensaje del contacto. Fuera de horario: se envía cuando escriben fuera del horario de atención.
            </p>
            {!autoReplySettings ? (
              <p style={{ color: '#667781' }}>Cargando...</p>
            ) : (
              <>
                <div style={{ marginBottom: '18px', padding: '12px', background: '#e1f5fe', borderRadius: '10px', border: '1px solid #b3e5fc' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '700', fontSize: '15px', color: '#01579b' }}>
                    <input type="checkbox" checked={!!autoReplySettings.global_bot_enabled} onChange={e => setAutoReplySettings(s => ({ ...s, global_bot_enabled: e.target.checked }))} />
                    🤖 IA Global Activa
                  </label>
                  <p style={{ margin: '4px 0 0 24px', fontSize: '12px', color: '#0277bd' }}>
                    Si se desactiva, ningún chat responderá con IA, incluso si tiene el bot encendido individualmente.
                  </p>
                </div>
                <div style={{ marginBottom: '18px', padding: '12px', background: '#f7f8f9', borderRadius: '10px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                    <input type="checkbox" checked={!!autoReplySettings.welcome_enabled} onChange={e => setAutoReplySettings(s => ({ ...s, welcome_enabled: e.target.checked }))} />
                    Mensaje de bienvenida
                  </label>
                  <textarea
                    value={autoReplySettings.welcome_message || ''}
                    onChange={e => setAutoReplySettings(s => ({ ...s, welcome_message: e.target.value }))}
                    placeholder="Ej: ¡Hola! Gracias por escribir. Te atenderemos en breve."
                    rows={2}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e9edef', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ marginBottom: '18px', padding: '12px', background: '#f7f8f9', borderRadius: '10px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                    <input type="checkbox" checked={!!autoReplySettings.away_enabled} onChange={e => setAutoReplySettings(s => ({ ...s, away_enabled: e.target.checked }))} />
                    Mensaje fuera de horario
                  </label>
                  <textarea
                    value={autoReplySettings.away_message || ''}
                    onChange={e => setAutoReplySettings(s => ({ ...s, away_message: e.target.value }))}
                    placeholder="Ej: Estamos fuera de horario. Te responderemos cuando volvamos."
                    rows={2}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e9edef', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                  <div style={{ display: 'flex', gap: '12px', marginTop: '10px', flexWrap: 'wrap' }}>
                    <label style={{ fontSize: '13px', color: '#54656f' }}>
                      De <input type="time" value={autoReplySettings.business_hours_start || '09:00'} onChange={e => setAutoReplySettings(s => ({ ...s, business_hours_start: e.target.value }))} style={{ marginLeft: '4px', padding: '4px' }} />
                    </label>
                    <label style={{ fontSize: '13px', color: '#54656f' }}>
                      A <input type="time" value={autoReplySettings.business_hours_end || '18:00'} onChange={e => setAutoReplySettings(s => ({ ...s, business_hours_end: e.target.value }))} style={{ marginLeft: '4px', padding: '4px' }} />
                    </label>
                    <label style={{ fontSize: '13px', color: '#54656f' }}>
                      Zona <select value={autoReplySettings.timezone || 'America/La_Paz'} onChange={e => setAutoReplySettings(s => ({ ...s, timezone: e.target.value }))} style={{ marginLeft: '4px', padding: '4px' }}>
                        <option value="America/La_Paz">La Paz</option>
                        <option value="America/Mexico_City">Ciudad de México</option>
                        <option value="America/Bogota">Bogotá</option>
                        <option value="America/Lima">Lima</option>
                        <option value="America/Argentina/Buenos_Aires">Buenos Aires</option>
                        <option value="America/Santiago">Santiago</option>
                        <option value="Europe/Madrid">Madrid</option>
                      </select>
                    </label>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginTop: '16px' }}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button type="button" onClick={disableAllAutoReplies} style={{ padding: '8px 14px', borderRadius: '10px', border: '1px solid #8696a0', background: '#fff', color: '#667781', cursor: 'pointer', fontSize: '13px' }} title="Desactiva bienvenida y fuera de horario (luego Guardar)">Desactivar todo</button>
                    <button type="button" onClick={resetAutoReplySettings} disabled={autoReplySaving} style={{ padding: '8px 14px', borderRadius: '10px', border: '1px solid #d32f2f', background: '#fff', color: '#d32f2f', cursor: 'pointer', fontSize: '13px' }} title="Valores por defecto y desactivar">Restablecer</button>
                  </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button type="button" onClick={() => setShowAutoReplyModal(false)} style={{ padding: '10px 18px', borderRadius: '10px', border: '1px solid #e9edef', background: '#fff', cursor: 'pointer' }}>Cancelar</button>
                    <button type="button" onClick={() => saveAutoReplySettings(autoReplySettings)} disabled={autoReplySaving} style={{ padding: '10px 20px', borderRadius: '10px', border: 'none', background: '#00a884', color: '#fff', cursor: 'pointer', fontWeight: '600' }}>{autoReplySaving ? 'Guardando...' : 'Guardar'}</button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal: Gestionar respuestas rápidas (lista + Editar / Eliminar) */}
      {showQuickReplyManageModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setShowQuickReplyManageModal(false)}
        >
          <div
            style={{ background: '#fff', borderRadius: '14px', padding: '20px', width: '90%', maxWidth: '480px', maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}
            onClick={e => e.stopPropagation()}
          >
            <h4 style={{ margin: '0 0 8px', fontSize: '16px' }}>Respuestas rápidas</h4>
            <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#667781' }}>En el chat escribe <strong>/</strong> para elegir una. Edita o elimina las que tengas.</p>
            <button
              type="button"
              onClick={openNewQuickReplyForm}
              style={{ alignSelf: 'flex-start', padding: '8px 16px', borderRadius: '10px', border: 'none', background: '#00a884', color: '#fff', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}
            >
              + Añadir respuesta rápida
            </button>
            <div style={{ flex: 1, overflowY: 'auto', marginTop: '14px', border: '1px solid #e9edef', borderRadius: '10px' }}>
              {(!quickReplies || quickReplies.length === 0) ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#667781', fontSize: '14px' }}>No hay respuestas rápidas. Añade una arriba.</div>
              ) : (
                quickReplies.map((q) => (
                  <div
                    key={q.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '12px 14px',
                      borderBottom: '1px solid #f0f2f5'
                    }}
                  >
                    {q.image_url && (
                      <img src={q.image_url} alt="" style={{ width: '40px', height: '40px', borderRadius: '8px', objectFit: 'cover', flexShrink: 0 }} onError={(e) => { e.target.style.display = 'none'; }} />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{q.text || '(sin texto)'}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => openEditQuickReplyForm(q)}
                      style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid #00a884', background: '#fff', color: '#00a884', cursor: 'pointer', fontSize: '13px' }}
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      onClick={() => { if (window.confirm('¿Eliminar esta respuesta rápida?')) { handleDeleteQuickReply(q.id); } }}
                      style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid #e74c3c', background: '#fff', color: '#e74c3c', cursor: 'pointer', fontSize: '13px' }}
                    >
                      Eliminar
                    </button>
                  </div>
                ))
              )}
            </div>
            <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setShowQuickReplyManageModal(false)}
                style={{ padding: '8px 16px', borderRadius: '10px', border: '1px solid #e9edef', background: '#fff', cursor: 'pointer' }}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Nueva / Editar respuesta rápida (formulario) */}
      {showQuickReplyModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001 }}
          onClick={() => { setShowQuickReplyModal(false); if (editingQuickReplyId) setShowQuickReplyManageModal(true); setEditingQuickReplyId(null); setNewQuickReplyText(''); setNewQuickReplyImageUrl(''); }}
        >
          <div
            style={{ background: '#fff', borderRadius: '14px', padding: '20px', width: '90%', maxWidth: '420px', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}
            onClick={e => e.stopPropagation()}
          >
            <h4 style={{ margin: '0 0 12px', fontSize: '16px' }}>{editingQuickReplyId ? 'Editar respuesta rápida' : 'Nueva respuesta rápida'}</h4>
            <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#667781' }}>Texto (obligatorio). Opcional: URL de imagen. En el chat escribe <strong>/</strong> para elegir.</p>
            <textarea
              value={newQuickReplyText}
              onChange={e => setNewQuickReplyText(e.target.value)}
              placeholder="Ej: Hola, gracias por escribir..."
              rows={3}
              style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #e9edef', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box' }}
              autoFocus
            />
            <input
              type="url"
              value={newQuickReplyImageUrl}
              onChange={e => setNewQuickReplyImageUrl(e.target.value)}
              placeholder="URL de imagen (opcional)"
              style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1px solid #e9edef', fontSize: '14px', marginTop: '10px', boxSizing: 'border-box' }}
            />
            {newQuickReplyImageUrl.trim() && (
              <div style={{ marginTop: '8px' }}>
                <img src={newQuickReplyImageUrl.trim()} alt="Vista previa" style={{ maxWidth: '100%', maxHeight: '120px', borderRadius: '8px', objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '14px' }}>
              <button
                type="button"
                onClick={() => { setShowQuickReplyModal(false); if (editingQuickReplyId) setShowQuickReplyManageModal(true); setEditingQuickReplyId(null); setNewQuickReplyText(''); setNewQuickReplyImageUrl(''); }}
                style={{ padding: '8px 16px', borderRadius: '10px', border: '1px solid #e9edef', background: '#fff', cursor: 'pointer' }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => {
                  if (newQuickReplyText.trim()) {
                    if (editingQuickReplyId) {
                      handleUpdateQuickReply(editingQuickReplyId, newQuickReplyText.trim(), newQuickReplyImageUrl.trim());
                      setShowQuickReplyModal(false);
                      setEditingQuickReplyId(null);
                      setNewQuickReplyText('');
                      setNewQuickReplyImageUrl('');
                    } else {
                      handleAddQuickReply(newQuickReplyText.trim(), newQuickReplyImageUrl.trim());
                      setShowQuickReplyModal(false);
                      setNewQuickReplyText('');
                      setNewQuickReplyImageUrl('');
                    }
                  }
                }}
                disabled={!newQuickReplyText.trim()}
                style={{ padding: '8px 18px', borderRadius: '10px', border: 'none', background: '#00a884', color: '#fff', cursor: 'pointer', fontWeight: '600' }}
              >
                {editingQuickReplyId ? 'Guardar cambios' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="toast-notification" style={{
          position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)',
          backgroundColor: toast.type === 'error' ? '#ef4444' : '#10b981', color: 'white',
          padding: '12px 24px', borderRadius: '8px', zIndex: 10000, fontWeight: '500',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)', animation: 'fadeIn 0.3s ease-in-out'
        }}>
          <span>{toast.type === 'error' ? '⚠️' : '✅'}</span> {toast.message}
        </div>
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translate(-50%, -20px); } to { opacity: 1; transform: translate(-50%, 0); } }
        @keyframes pulse-green {
          0% { box-shadow: 0 0 0 0 rgba(0, 230, 118, 0.7); }
          70% { box-shadow: 0 0 0 10px rgba(0, 230, 118, 0); }
          100% { box-shadow: 0 0 0 0 rgba(0, 230, 118, 0); }
        }
      `}</style>
    </div>
  );
}

export default App;
